import type { Translate } from "@similarweb/i18n-provider";
import { EWizardLegendItemState } from "@similarweb/ui-components";

import { DEFAULT_SEARCH_ENGINE_FULL } from "app/components/campaigns/searchEngine/SearchEngineUtils";
import { translate } from "app/filters/i18nFilter";
import type {
    ICampaignsWizardState,
    IKeywordsStep,
    IReviewStep,
    ISearchEnginesStep,
    IYourSiteStep,
} from "app/pages/digital-marketing/keywords-campaigns/wizard/campaigns-wizard/types";
import { ECampaignPagesError } from "app/pages/digital-marketing/keywords-campaigns/wizard/campaigns-wizard/types";
import type { WebsitePageSearchType } from "pro-features/components/AutocompleteWebsites/types";

export const LOCATION_SORT_ORDER = [
    "Country",
    "Province",
    "State",
    "Territory",
    "Union Territory",
    "Canton",
    "Okrug",
    "Governorate",
    "Prefecture",
    "Region",
    "Autonomous Community",
    "County",
    "District",
    "City",
    "City Region",
    "Municipality",
    "Borough",
    "Neighborhood",
    "Postal Code",
] as const;

export const REGULAR_FOOTER_WIDTH = 700;

export const WebsitePageSearchTypeNames: Record<string, WebsitePageSearchType> = {
    MainDomain: "domain",
    Folder: "folder",
    ExactUrl: "exact",
    Subdomain: "subdomain",
};

export const WebsitePageSearchTypeNumbers: Record<WebsitePageSearchType, number> = {
    subdomain: 0,
    domain: 0, // Production bug fix - that we want now every domain to be treated as a subdomain(meaning including all subdomains)
    folder: 2,
    exact: 3,
};

export interface IKeywordsCampaignsPageValidator {
    isPageHasErrors: () => boolean;
}
export interface ISimilarSiteSuggestion {
    Domain: string;
    Favicon: string;
}

const selectSiteStep = (translateFn: Translate): IYourSiteStep => ({
    fields: {
        selectedSite: {
            value: null,
            hasError: true,
        },
        entities: {
            value: [],
            hasError: false,
        },
        competitors: {
            value: [],
            hasError: false,
        },
    },

    legendData: {
        get title() {
            return translate("seo.keywordsCampaign.wizard.navigation.selectSiteStep.title");
        },
        itemState: EWizardLegendItemState.NOT_VISITED,
        get clickableItemTooltip() {
            return translate("seo.keywordsCampaign.wizard.navigation.selectSiteStep.tooltipClickable");
        },
        get disabledItemTooltip() {
            return translate("seo.keywordsCampaign.wizard.navigation.common.tooltipDisabled");
        },
    },

    pageError: ECampaignPagesError.EMPTY_OR_DUPLICATE_SITE_STEP_ERROR,
    shouldShowKeywordCounter: false,
});

const searchEnginesStep = (translateFn: Translate): ISearchEnginesStep => ({
    fields: {
        searchEngines: {
            value: [DEFAULT_SEARCH_ENGINE_FULL],
            hasError: false,
        },
        lastSearchEngineID: {
            value: 1,
            hasError: false,
        },
    },
    legendData: {
        get title() {
            return translate("home.dashboards.wizard.locationPicker.title");
        },
        itemState: EWizardLegendItemState.NOT_VISITED,
        get clickableItemTooltip() {
            return translate("seo.keywordsCampaign.wizard.navigation.searchEnginesStep.tooltipClickable");
        },
        get disabledItemTooltip() {
            return translate("seo.keywordsCampaign.wizard.navigation.common.tooltipDisabled");
        },
    },
    pageError: null,
    shouldShowKeywordCounter: false,
});

const keywordsStep = (translateFn: Translate): IKeywordsStep => ({
    fields: {
        keywordSources: {
            value: [{ id: "manual", type: "manual", keywords: [] }],
            hasError: false,
        },
        // Step 3-only draft used to restore the standard wizard's GSC modal state on reopen.
        // Step 2 search engines remain the source of truth for allowed markets/locations.
        standardGscDraft: {
            value: null,
            hasError: false,
        },
    },
    legendData: {
        get title() {
            return translate("seo.keywordsCampaign.wizard.navigation.keywordsStep.title");
        },
        itemState: EWizardLegendItemState.NOT_VISITED,
        get clickableItemTooltip() {
            return translate("seo.keywordsCampaign.wizard.navigation.keywordsStep.tooltipClickable");
        },
        get disabledItemTooltip() {
            return translate("seo.keywordsCampaign.wizard.navigation.common.tooltipDisabled");
        },
    },
    pageError: ECampaignPagesError.EMPTY_TEXT_AREA_STEP_ERROR,
    shouldShowKeywordCounter: true,
});

const reviewStep = (translateFn: Translate): IReviewStep => ({
    fields: {
        projectName: {
            value: null,
            hasError: true,
        },
        isSubscribedToEmail: {
            value: true,
            hasError: false,
        },
        keywordsAndTags: {
            value: [],
            hasError: false,
        },
    },
    legendData: {
        get title() {
            return translate("seo.keywordsCampaign.wizard.navigation.reviewStep.title");
        },
        itemState: EWizardLegendItemState.NOT_VISITED,
        get clickableItemTooltip() {
            return translate("seo.keywordsCampaign.wizard.navigation.reviewStep.tooltipClickable");
        },
        get disabledItemTooltip() {
            return translate("seo.keywordsCampaign.wizard.navigation.common.tooltipDisabled");
        },
    },
    pageError: null,
    shouldShowKeywordCounter: true,
});

export const CampaignsFields = {
    SELECTED_SITE: "selectedSite",
    PROJECT_NAME: "projectName",
    IS_SUBSCRIBED_TO_EMAIL: "isSubscribedToEmail",
    COMPETITORS: "competitors",
    KEYWORDS_AND_TAGS: "keywordsAndTags",
    SEARCH_ENGINES: "searchEngines",
    LAST_SEARCH_ENGINE_ID: "lastSearchEngineID",
    WEB_ENTITIES: "entities",
} as const;

export const defaultWizardState = (translateFn: Translate): ICampaignsWizardState => ({
    pages: [
        selectSiteStep(translateFn),
        searchEnginesStep(translateFn),
        keywordsStep(translateFn),
        reviewStep(translateFn),
    ],
    campaignsFields: CampaignsFields,
});

export const DEMO_CAMPAIGNS_URL = `api/rankTracker/demo/campaigns`;
export const getSingleCampaignURL = (isDemo: boolean) =>
    `${isDemo ? "api/rankTracker/demo" : "api/rankTracker"}/campaign`;

export const wizardRankTrackerErrorMessagesMapping: Partial<Record<ECampaignPagesError, string>> = {
    get [ECampaignPagesError.MAX_LENGTH]() {
        return translate("seo.keywordsCampaign.wizard.selectSiteStep.projectName.input.error.maxLength");
    },
    get [ECampaignPagesError.MINIMUM_LENGTH]() {
        return translate("seo.keywordsCampaign.wizard.selectSiteStep.projectName.input.error.minimumLength");
    },
    get [ECampaignPagesError.DUPLICATE_NAME]() {
        return translate("seo.keywordsCampaign.wizard.selectSiteStep.selectedSite.input.error.duplicateProjectName");
    },
    get [ECampaignPagesError.EMPTY_FIELD]() {
        return translate("seo.keywordsCampaign.wizard.selectSiteStep.selectedSite.input.error.emptyField");
    },
    get [ECampaignPagesError.INVALID_TEXT]() {
        return translate("seo.keywordsCampaign.wizard.selectSiteStep.selectedSite.input.error.invalidUrl");
    },
    get [ECampaignPagesError.SITE_MAX_LENGTH]() {
        return translate("seo.keywordsCampaign.wizard.selectSiteStep.selectedSite.input.error.maxLength");
    },
    get [ECampaignPagesError.INVALID_CAMPAIGN_NAME]() {
        return translate("seo.keywordsCampaign.wizard.selectSiteStep.projectName.input.error.invalidProjectName");
    },
    get [ECampaignPagesError.INVALID_CAMPAIGN_NAME_BOUNDARIES]() {
        return translate(
            "seo.keywordsCampaign.wizard.selectSiteStep.projectName.input.error.invalidProjectNameBoundaries",
        );
    },
};

export const wizardErrorTooltipMapping: Partial<Record<ECampaignPagesError, string>> = {
    get [ECampaignPagesError.SEARCH_ENGINE_HAS_NOT_KEYWORDS]() {
        return translate("campaigns.wizard.searchEngine.error.tooltip");
    },
};

export const TOAST_MESSAGE_CREATE_CAMPAIGN_GENERAL_ERROR =
    "seo.keywordsCampaign.wizard.addingKeywordsStep.createCampaign.error";
export const TOAST_MESSAGE_URL_ERROR = "seo.keywordsCampaign.wizard.addingKeywordsStep.createCampaign.urlError";
export const TOAST_MESSAGE_URL_ERROR_TITLE = "RT.wizard.addingKeywordsStep.createCampaign.urlError.title";
export const TOAST_MESSAGE_URL_ERROR_BTN_TEXT = "RT.wizard.addingKeywordsStep.createCampaign.urlError.closeBtn";
export const SITE_PREFIX_ERROR = "Site must be a valid URL starting with";
